/**
 * ScanPanelContainer - resolves the scan source catalogue, then renders ScanPanel.
 *
 * Kept separate from ScanPanel so the panel itself stays presentational and
 * testable. The catalogue (jurisdiction indexes + the org's data sources) is the
 * same cached one the Research surface uses, so opening a scan costs no extra
 * network once Research has loaded it.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { loadSourceCatalogue } from '../Research/sourceCatalogue';
import type { ResearchSource } from '../../store/actions/ChatAction';
import ScanPanel, { ScanTarget } from './ScanPanel';
import Spinner from '../shared/TailwindComponents/Spinner';

export interface ScanPanelContainerProps {
  target: ScanTarget;
  language?: string | null;
  documentNames?: Record<number, string>;
  onClose?: () => void;
}

const ScanPanelContainer: React.FC<ScanPanelContainerProps> = ({ target, language, documentNames, onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [jurisdictionSources, setJurisdictionSources] = useState<ResearchSource[]>([]);
  const [userDatasources, setUserDatasources] = useState<ResearchSource[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadSourceCatalogue(dispatch)
      .catch(() => ({ jurisdiction_sources: [], user_datasources: [] }))
      .then((catalogue) => {
        if (cancelled) return;
        setJurisdictionSources(catalogue.jurisdiction_sources || []);
        setUserDatasources(catalogue.user_datasources || []);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [dispatch]);

  if (!loaded) {
    return (
      <Spinner size="md" centered ariaLabel={t('Loading…')} />
    );
  }

  return (
    <ScanPanel
      target={target}
      jurisdictionSources={jurisdictionSources}
      userDatasources={userDatasources}
      language={language}
      documentNames={documentNames}
      onClose={onClose}
    />
  );
};

export default ScanPanelContainer;
